import { notification } from 'antd';
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  createHttpLink,
  from,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
// Config
import ENV from 'api/env';
// Helpers
import { handleAuthError } from 'helpers/auth';
import { getAuthUserFromCookies } from 'helpers/cookies';

const mergeJobsById = (
  existingItems: any[] = [],
  incomingItems: any[] = []
) => {
  const existingJobIds = new Set(existingItems.map((job) => job.jobId));
  const newItems = incomingItems.filter(
    (job) => !existingJobIds.has(job.jobId)
  );

  return [...existingItems, ...newItems];
};

const httpLink = createHttpLink({
  uri: ENV.RECRUITMENT_API_HOST,
});

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      const errorMessages = graphQLErrors.map(({ message }) => message);

      if (errorMessages.length) {
        return handleAuthError({ operation, forward, errorMessages });
      }
    }

    if (networkError) {
      const errorMessage =
        typeof networkError === 'string'
          ? networkError
          : JSON.stringify(networkError);

      notification.error({
        description:
          "Seems like You've lost the Internet connection. Try connecting a different Wi-Fi, or check the network cables, modem and router.",
        message: 'Network error',
        duration: 15,
      });

      console.log(`[Network error]: ${errorMessage}`);
    }
  }
);

const authLink = new ApolloLink((operation, forward) => {
  const authUserFromCookies = getAuthUserFromCookies();
  const tokenFromCookies = authUserFromCookies?.accessToken || '';

  operation.setContext(({ headers }: { headers: Record<string, string> }) => ({
    headers: {
      ...headers,
      authorization: tokenFromCookies ? `Bearer ${tokenFromCookies}` : '',
    },
  }));

  return forward(operation);
});

export const recruitmentClient = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          listImportCsvJobs: {
            keyArgs: false,
            merge(existing, incoming, { args }) {
              const existingItems = existing?.items || [];
              const incomingItems = incoming?.items || [];

              if (args?.nextToken) {
                return {
                  ...incoming,
                  items: mergeJobsById(existingItems, incomingItems),
                };
              }

              const incomingJobIds = new Set(
                incomingItems.map((job: { jobId: string }) => job.jobId)
              );
              const preservedLoadedItems = existingItems.filter(
                (job: { jobId: string }) => !incomingJobIds.has(job.jobId)
              );

              return {
                ...incoming,
                items: [...incomingItems, ...preservedLoadedItems],
                nextToken: existingItems.length
                  ? existing?.nextToken || incoming?.nextToken || null
                  : incoming?.nextToken || null,
              };
            },
          },
        },
      },
    },
  }),
  link: from([errorLink, authLink]).concat(httpLink),
});
